import React, { useEffect, useState } from "react";
import { Space, Typography, Row, Col } from "antd";
import { D3PieChart, D3BarChart } from "../../../components/charts";
import { getDashboardData } from "../../../api/labiq/SampleApi";
import SampleModal from "../../lab/tracking/components/SampleModal";
import { Sample } from "../../../types/lab/SampleTypes";
import SectionCard from "../../../components/shared/SectionCard";
import InlineLoading from "../../../components/shared/InlineLoading";
import EmptyState from "../../../components/shared/EmptyState";
import StatusTag from "../../../components/shared/StatusTag";
import DataTable from "../../../components/shared/DataTable";
import { KPIItem } from "../components/DashboardKPIStrip";

const { Text } = Typography;

// Chart colours — used only for Recharts data visualisation
const CHART_COLORS = ["#0088FE", "#00C49F", "#FFBB28", "#FF8042", "#8884D8", "#82CA9D", "#FFC658", "#FF6B6B", "#4ECDC4", "#45B7D1"];

interface LabDashboardProps {
	startDate: Date;
	endDate: Date;
	refreshToken?: number;
	onKPIChange?: (kpis: KPIItem[]) => void;
}

const LabDashboard: React.FC<LabDashboardProps> = ({ startDate, endDate, refreshToken = 0, onKPIChange }) => {
	const [loading, setLoading] = useState({ overview: true, samples: true, batches: true, history: true });
	const [selectedSample, setSelectedSample] = useState<Sample | null>(null);


	const [sampleData, setSampleData] = useState<any>({
		samples: [],
		totalSamples: 0,
		samplesPerStatus: {},
		samplesStatusChart: [],
		recentSamples: [],
		analysisRequestsChart: [],
	});


	useEffect(() => {
		loadDashboardData();
	}, []);

	const loadDashboardData = async () => {
		setLoading({ overview: true, samples: true, batches: true, history: true });
		await fetchLabOverview();
	};

	const fetchLabOverview = async () => {
		try {
			const { totals, samples } = await getDashboardData(startDate, endDate);

			await Promise.all([processSamples(samples)]);
		} catch (error) {
			console.error("Error loading lab overview data:", error);
			setLoading({ overview: false, samples: false, batches: false, history: false });
		} finally {
			setLoading((prev) => ({ ...prev, overview: false }));
		}
	};

	const processSamples = async (samples: Sample[]) => {
		try {
			const statusCounts: { [key: string]: number } = {};
			samples.forEach((sample) => {
				const status = sample.sampleOA?.status || "Unknown";
				statusCounts[status] = (statusCounts[status] || 0) + 1;
			});

			const analysisRequestCounts: { [key: string]: number } = {};
			samples.forEach((sample) => {
				if (sample.sampleOA?.analysis_requested?.length) {
					sample.sampleOA.analysis_requested.forEach((analysis) => {
						analysisRequestCounts[analysis] = (analysisRequestCounts[analysis] || 0) + 1;
					});
				}
			});

			// Transform data for Recharts format
			const samplesStatusChart = Object.keys(statusCounts).map((key) => ({
				name: key,
				value: statusCounts[key],
			}));

			const analysisRequestsChart = Object.keys(analysisRequestCounts).map((key) => ({
				name: key,
				value: analysisRequestCounts[key],
			}));

			setSampleData({
				samples,
				totalSamples: samples.length,
				samplesPerStatus: statusCounts,
				samplesStatusChart,
				recentSamples: samples.slice(0, 10),
				analysisRequestsChart,
			});

		} catch (error) {
			console.error("Error processing sample data:", error);
		} finally {
			setLoading((prev) => ({ ...prev, samples: false }));
		}
	};



	const formatDate = (date: Date | string | undefined) => {
		if (!date) return "—";
		return new Date(date).toLocaleDateString();
	};

	const sampleColumns = [
		{ title: "Sample ID", dataIndex: "sampleID", key: "sampleID", width: 150 },
		{ title: "Client", dataIndex: "client", key: "client", width: 150 },
		{
			title: "Client Sample ID",
			dataIndex: ["sampleOA", "client_sampleID"],
			key: "clientSampleID",
			width: 150,
		},
		{
			title: "Sample Type",
			dataIndex: ["sampleOA", "sampleType"],
			key: "sampleType",
			width: 120,
		},
		{
			title: "Status",
			dataIndex: ["sampleOA", "status"],
			key: "status",
			width: 120,
			render: (status: string) => <StatusTag status={status} />,
		},
		{
			title: "Analysis",
			dataIndex: ["sampleOA", "analysis_requested"],
			key: "analysis",
			width: 200,
			render: (analyses: string[]) =>
				analyses?.length ? (
					<Space size={4} wrap>
						{analyses.map((analysis, index) => (
							<Text key={index} style={{ fontSize: 12, background: "var(--mg-bg-subtle)", border: "1px solid var(--mg-border)", borderRadius: 4, padding: "1px 6px" }}>
								{analysis}
							</Text>
						))}
					</Space>
				) : (
					<Text type="secondary">—</Text>
				),
		},
		{
			title: "Created Date",
			dataIndex: "created_at",
			key: "created_at",
			width: 120,
			render: (date: string) => formatDate(date),
		},
	];

	return (
		<div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
			{/* Charts */}
			<Row gutter={16}>
				<Col xs={24} lg={12}>
					<SectionCard title="Sample Status Distribution" style={{ height: "100%" }}>
						{loading.samples ? (
							<InlineLoading minHeight={300} message="Loading chart…" />
						) : (
							<D3PieChart
								data={sampleData.samplesStatusChart}
								dataKey="value"
								nameKey="name"
								colors={CHART_COLORS}
								outerRadius={80}
								showLegend
							/>
						)}
					</SectionCard>
				</Col>
				<Col xs={24} lg={12}>
					<SectionCard title="Analysis Requests Distribution" style={{ height: "100%" }}>
						{loading.samples ? (
							<InlineLoading minHeight={300} message="Loading chart…" />
						) : (
							<D3BarChart
								data={sampleData.analysisRequestsChart}
								xKey="name"
								yKey="value"
								colors={CHART_COLORS}
								barLabel="Count"
								showLegend
							/>
						)}
					</SectionCard>
				</Col>
			</Row>

			{/* Recent samples */}
			<SectionCard title="Recent Samples" subtitle="Last 10 samples" noPadding>
				{loading.samples ? (
					<InlineLoading minHeight={200} message="Loading samples…" />
				) : sampleData.recentSamples.length > 0 ? (
					<DataTable
						bare
						columns={sampleColumns}
						dataSource={sampleData.recentSamples}
						rowKey={(r: Sample) => r.sampleID}
						pagination={{ pageSize: 5 }}
						onRowClick={(record: Sample) => setSelectedSample(record)}
					/>
				) : (
					<EmptyState title="No samples found" description="No samples in the selected date range" style={{ padding: "32px 0" }} />
				)}
			</SectionCard>

			{selectedSample && <SampleModal isOpen={!!selectedSample} onClose={() => setSelectedSample(null)} onFormSubmission={() => setSelectedSample(null)} patient={null} sample={selectedSample} />}
		</div>
	);
};


export default LabDashboard;
